import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Radio
} from '@mui/material';
import {
  Add,
  Delete,
  DragIndicator,
  TextFields,
  CheckBox,
  RadioButtonChecked,
  ArrowDropDownCircle,
  DateRange,
  Notes,
  Save,
  Preview,
  ArrowBack,
  ArrowForward,
  ContentCopy,
  Settings
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Intake Form Builder - No signatures, no consent requirements
// Used to add custom questions to the PUBLIC inquiry form clients fill out

const ELEMENT_TYPES = [
  { type: 'text', label: 'Short Text', icon: <TextFields />, description: 'Single line text input' },
  { type: 'textarea', label: 'Long Text', icon: <Notes />, description: 'Multi-line text area' },
  { type: 'checkbox', label: 'Checkboxes', icon: <CheckBox />, description: 'Multiple choice (select many)' },
  { type: 'radio', label: 'Radio Buttons', icon: <RadioButtonChecked />, description: 'Single choice' },
  { type: 'dropdown', label: 'Dropdown', icon: <ArrowDropDownCircle />, description: 'Select from list' },
  { type: 'date', label: 'Date', icon: <DateRange />, description: 'Date picker' },
  { type: 'section', label: 'Section Header', icon: <Notes />, description: 'Group related fields' },
];

const IntakeFormBuilder = ({ onSave, onCancel, initialTemplate = null }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formTitle, setFormTitle] = useState(initialTemplate?.title || '');
  const [formDescription, setFormDescription] = useState(initialTemplate?.description || '');
  const [elements, setElements] = useState(initialTemplate?.form_config?.elements || []);
  const [settings, setSettings] = useState({
    enableEmail: initialTemplate?.enable_email || false,
    ...initialTemplate?.form_config?.settings
  });
  const [selectedElement, setSelectedElement] = useState(null);
  const [errors, setErrors] = useState({});

  const steps = ['Form Setup', 'Build Form', 'Settings & Preview'];

  // Generate unique ID
  const generateId = () => `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Add new element
  const handleAddElement = (type) => {
    const newElement = {
      id: generateId(),
      type,
      label: `New ${ELEMENT_TYPES.find(e => e.type === type)?.label || 'Field'}`,
      required: false,
      placeholder: '',
      helpText: '',
      options: type === 'checkbox' || type === 'radio' || type === 'dropdown' 
        ? ['Option 1', 'Option 2'] 
        : undefined,
    };
    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
  };

  // Update element
  const handleUpdateElement = (id, updates) => {
    setElements(elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    ));
  };

  // Delete element
  const handleDeleteElement = (id) => {
    setElements(elements.filter(el => el.id !== id));
    if (selectedElement === id) {
      setSelectedElement(null);
    }
  };

  // Duplicate element
  const handleDuplicateElement = (element) => {
    const newElement = {
      ...element,
      id: generateId(),
      label: `${element.label} (Copy)`
    };
    const index = elements.findIndex(el => el.id === element.id);
    const newElements = [...elements];
    newElements.splice(index + 1, 0, newElement);
    setElements(newElements);
  };

  // Drag and drop
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(elements);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setElements(items);
  };

  // Validation
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      if (!formTitle.trim()) newErrors.title = 'Form title is required';
    }
    
    if (step === 1) {
      if (elements.length === 0) newErrors.elements = 'Add at least one form element';
      elements.forEach((el, idx) => {
        if (!el.label.trim()) newErrors[`element_${idx}`] = 'All fields must have labels';
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Save form
  const handleSave = () => {
    if (!validateStep(0) || !validateStep(1)) {
      setActiveStep(0);
      return;
    }

    const formData = {
      title: formTitle,
      description: formDescription,
      form_type: 'intake', // Always intake for this builder
      enable_email: settings.enableEmail,
      enable_id_upload: false, // Intake forms don't need ID upload
      form_config: {
        elements,
        settings: {
          ...settings,
          formType: 'intake'
        },
        metadata: {
          title: formTitle,
          description: formDescription,
          enableEmail: settings.enableEmail,
          enableIdUpload: false
        }
      }
    };

    onSave(formData);
  };

  // Render element palette
  const renderElementPalette = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Add Form Elements
      </Typography>
      <Grid container spacing={1}>
        {ELEMENT_TYPES.map(({ type, label, icon }) => (
          <Grid item xs={6} sm={4} md={3} key={type}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={icon}
              onClick={() => handleAddElement(type)}
              sx={{ 
                justifyContent: 'flex-start',
                textTransform: 'none',
                py: 1
              }}
            >
              {label}
            </Button>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  // Render element editor
  const renderElementEditor = (element, index) => (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2, 
        border: selectedElement === element.id ? '2px solid' : '1px solid',
        borderColor: selectedElement === element.id ? 'primary.main' : 'grey.300'
      }}
      onClick={() => setSelectedElement(element.id)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <DragIndicator sx={{ mr: 1, color: 'text.secondary', cursor: 'grab' }} />
        <Chip 
          label={ELEMENT_TYPES.find(e => e.type === element.type)?.label || element.type} 
          size="small" 
          color="primary"
          variant="outlined"
          sx={{ mr: 1 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Duplicate">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDuplicateElement(element); }}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteElement(element.id); }}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Field Label"
            value={element.label}
            onChange={(e) => handleUpdateElement(element.id, { label: e.target.value })}
            size="small"
            error={!!errors[`element_${index}`]}
            helperText={errors[`element_${index}`]}
          />
        </Grid>
        
        {element.type !== 'section' && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Placeholder Text"
                value={element.placeholder || ''}
                onChange={(e) => handleUpdateElement(element.id, { placeholder: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Help Text"
                value={element.helpText || ''}
                onChange={(e) => handleUpdateElement(element.id, { helpText: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={element.required || false}
                    onChange={(e) => handleUpdateElement(element.id, { required: e.target.checked })}
                  />
                }
                label="Required field"
              />
            </Grid>
          </>
        )}

        {/* Options for checkbox, radio, dropdown */}
        {(element.type === 'checkbox' || element.type === 'radio' || element.type === 'dropdown') && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Options:</Typography>
            {(element.options || []).map((option, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TextField
                  size="small"
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...(element.options || [])];
                    newOptions[idx] = e.target.value;
                    handleUpdateElement(element.id, { options: newOptions });
                  }}
                  sx={{ flexGrow: 1, mr: 1 }}
                  placeholder={`Option ${idx + 1}`}
                />
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => {
                    const newOptions = element.options.filter((_, i) => i !== idx);
                    handleUpdateElement(element.id, { options: newOptions });
                  }}
                  disabled={(element.options || []).length <= 1}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => {
                const newOptions = [...(element.options || []), `Option ${(element.options || []).length + 1}`];
                handleUpdateElement(element.id, { options: newOptions });
              }}
            >
              Add Option
            </Button>
          </Grid>
        )}
      </Grid>
    </Paper>
  );

  // Render form preview
  const renderPreview = () => (
    <Paper sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
      <Typography variant="h5" gutterBottom>{formTitle || 'Untitled Form'}</Typography>
      {formDescription && (
        <Typography color="text.secondary" sx={{ mb: 3 }}>{formDescription}</Typography>
      )}
      <Divider sx={{ mb: 3 }} />
      
      {elements.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No form elements added yet
        </Typography>
      ) : (
        elements.map((element) => (
          <Box key={element.id} sx={{ mb: 3 }}>
            {element.type === 'section' ? (
              <Typography variant="h6" sx={{ mt: 2, mb: 1, borderBottom: '1px solid', borderColor: 'grey.300', pb: 1 }}>
                {element.label}
              </Typography>
            ) : (
              <>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 500 }}>
                  {element.label}
                  {element.required && <span style={{ color: 'red' }}> *</span>}
                </Typography>
                {element.helpText && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {element.helpText}
                  </Typography>
                )}
                
                {element.type === 'text' && (
                  <TextField fullWidth placeholder={element.placeholder} size="small" disabled />
                )}
                {element.type === 'textarea' && (
                  <TextField fullWidth multiline rows={3} placeholder={element.placeholder} disabled />
                )}
                {element.type === 'date' && (
                  <TextField type="date" size="small" disabled sx={{ minWidth: 200 }} />
                )}
                {element.type === 'dropdown' && (
                  <FormControl fullWidth size="small" disabled>
                    <Select value="" displayEmpty>
                      <MenuItem value="" disabled>Select an option</MenuItem>
                      {(element.options || []).map((opt, idx) => (
                        <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {element.type === 'checkbox' && (
                  <Box>
                    {(element.options || []).map((opt, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={<Checkbox disabled />}
                        label={opt}
                      />
                    ))}
                  </Box>
                )}
                {element.type === 'radio' && (
                  <Box>
                    {(element.options || []).map((opt, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={<Radio disabled />}
                        label={opt}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        ))
      )}
    </Paper>
  );

  // Step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Custom Inquiry Questions</strong> - Add custom questions to your public inquiry form 
              that potential clients will see when requesting an appointment. Perfect for collecting 
              style preferences, medical history, availability, or any other information you need before booking.
            </Alert>
            
            <TextField
              fullWidth
              label="Form Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              sx={{ mb: 2 }}
              placeholder="e.g., Custom Questions, Client Intake, Style Preferences"
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Form Description (Optional)"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Describe what this form is for..."
            />
          </Box>
        );
        
      case 1:
        return (
          <Box>
            {errors.elements && (
              <Alert severity="error" sx={{ mb: 2 }}>{errors.elements}</Alert>
            )}
            
            {/* Info about standard fields already included */}
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Don't duplicate these fields</strong> â€” they're already in the standard inquiry form:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                <Chip label="Name" size="small" variant="outlined" />
                <Chip label="Email" size="small" variant="outlined" />
                <Chip label="Phone" size="small" variant="outlined" />
                <Chip label="How did you hear about me?" size="small" variant="outlined" />
                <Chip label="First tattoo?" size="small" variant="outlined" />
                <Chip label="Tattoo idea description" size="small" variant="outlined" />
                <Chip label="Placement" size="small" variant="outlined" />
                <Chip label="Size" size="small" variant="outlined" />
                <Chip label="Budget range" size="small" variant="outlined" />
                <Chip label="Timeframe" size="small" variant="outlined" />
                <Chip label="Additional notes" size="small" variant="outlined" />
                <Chip label="Reference images" size="small" variant="outlined" />
              </Box>
              <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
                Add questions like: skin sensitivity, allergies, medical conditions, style preferences, availability, etc.
              </Typography>
            </Alert>
            
            {renderElementPalette()}
            
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Form Elements ({elements.length})
            </Typography>
            
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="elements">
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {elements.map((element, index) => (
                      <Draggable key={element.id} draggableId={element.id} index={index}>
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            {renderElementEditor(element, index)}
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
            
            {elements.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
                <Typography color="text.secondary">
                  Click on an element type above to add it to your form
                </Typography>
              </Paper>
            )}
          </Box>
        );
        
      case 2:
        return (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <Settings sx={{ mr: 1 }} />
                    Form Settings
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.enableEmail}
                        onChange={(e) => setSettings({ ...settings, enableEmail: e.target.checked })}
                      />
                    }
                    label="Send confirmation email to client after submission"
                  />
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>How to use:</strong> After saving, go to Inquiry Form Settings to select this 
                      form as your custom inquiry template. The questions you add here will appear on your 
                      public inquiry form that clients fill out.
                    </Typography>
                  </Alert>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Preview sx={{ mr: 1 }} />
                    Form Preview
                  </Typography>
                </Box>
                {renderPreview()}
              </Grid>
            </Grid>
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            {initialTemplate ? 'Edit Custom Inquiry Questions' : 'Create Custom Inquiry Questions'}
          </Typography>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<Save />}
              size="large"
            >
              Save Custom Questions
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default IntakeFormBuilder;